import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, DollarSign, Users, Clock } from "lucide-react";

const leagues = [
  {
    name: "Tuesday Ladies",
    subtitle: "18 & 9 Holes",
    start: "April 28th",
    banquet: "October 6th",
    fee: "$60",
    color: "border-[#C9A96E]",
  },
  {
    name: "Wednesday Ladies Twilight League",
    subtitle: "18 & 9 Holes",
    start: "April 22nd",
    banquet: "October 7th",
    fee: "$60",
    color: "border-[#C9A96E]",
  },
  {
    name: "Wednesday Senior Men's League",
    subtitle: null,
    start: "April 29th",
    banquet: "September 30th",
    fee: "$60",
    color: "border-[#1B2A4A]",
  },
  {
    name: "Thursday Men's Twilight League",
    subtitle: "18 & 9 Holes",
    start: "April 23rd",
    banquet: "October 8th",
    fee: "$90",
    color: "border-[#1B2A4A]",
  },
];

const Leagues = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[450px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/1325682/pexels-photo-1325682.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Play Together</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">2026 Golf Leagues</h1>
          <p className="text-white/70 text-lg mt-4 max-w-2xl">
            Join one of our seasonal leagues and enjoy weekly rounds with a great group of golfers.
          </p>
        </div>
      </section>

      {/* League Cards */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">2026 Season</span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-4">League Schedule & Fees</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              All leagues run weekly throughout the season and include an end-of-season banquet. Register early to secure your spot.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {leagues.map((league) => (
              <div key={league.name} className={`bg-white rounded-2xl shadow-sm border-l-4 ${league.color} border border-gray-100 p-8 hover:shadow-md transition-shadow`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-serif font-bold text-[#1B2A4A]">{league.name}</h3>
                    {league.subtitle && (
                      <p className="text-sm text-gray-400 mt-0.5">{league.subtitle}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#1B2A4A]">{league.fee}</div>
                    <div className="text-xs text-gray-400">per season</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={15} className="text-[#C9A96E] shrink-0" />
                    <span><span className="font-medium">Starts:</span> {league.start}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users size={15} className="text-[#C9A96E] shrink-0" />
                    <span><span className="font-medium">Banquet:</span> {league.banquet}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Register CTA */}
          <div className="bg-[#f8f7f4] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-serif font-bold text-[#1B2A4A] mb-1">Ready to Join a League?</h3>
              <p className="text-gray-500 text-sm">Contact the pro shop or call us to register for the 2026 season.</p>
            </div>
            <div className="flex gap-4 shrink-0">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B2A4A] text-white text-sm font-semibold rounded-md hover:bg-[#2a3d62] transition-colors"
              >
                Register Now <ArrowRight size={16} />
              </Link>
              <a
                href="tel:5196768772"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#1B2A4A] text-[#1B2A4A] text-sm font-semibold rounded-md hover:bg-[#1B2A4A] hover:text-white transition-colors"
              >
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Visual section */}
      <section className="py-20 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1680157593682-dfce4735381d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Golf league players"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Why League Golf</span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">More Than Just a Round</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Willow Ridge leagues are a fantastic way to improve your game while enjoying the social side of golf. Whether you're a seasoned player or just getting started, there's a league for you.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Clock, label: "Weekly Tee Times", desc: "Consistent scheduling all season long" },
                  { icon: Users, label: "Social Community", desc: "Meet and play with fellow golf enthusiasts" },
                  { icon: DollarSign, label: "Great Value", desc: "Season-long play starting at just $60" },
                  { icon: Calendar, label: "Season Banquet", desc: "Celebrate the season with prizes & dinner" },
                ].map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-lg bg-[#C9A96E]/10 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-[#C9A96E]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1B2A4A] text-sm">{label}</div>
                      <div className="text-gray-500 text-sm">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">Join a League This Season</h2>
          <p className="text-white/60 mb-8">Call or visit the pro shop to reserve your spot in any of our 2026 leagues.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors"
            >
              Contact Us <ArrowRight size={18} />
            </Link>
            <a
              href="tel:5196768772"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors"
            >
              Call (519) 676-8772
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Leagues;
